import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Stack,
  Divider,
  Button,
  Card,
  CardContent,
  Chip,
  Avatar,
} from '@mui/material';
import {
  Apple,
  Android,
  GitHub,
  LinkedIn,
  Twitter,
  Email,
  Business,
  Security,
  Speed,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

const Footer: React.FC = () => {
  return (
    <Box sx={{ bgcolor: '#0f0f23', color: 'white', py: 8 }}>
      <Container maxWidth="lg">
        {/* Main Footer Content */}
        <Grid container spacing={6}>
          {/* Company Info */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                bgcolor: 'transparent',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 4,
                overflow: 'visible',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ mb: 4 }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      mb: 3,
                    }}
                  >
                    <Business />
                  </Avatar>
                  <Typography
                    variant="h4"
                    component="h3"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      color: 'white',
                    }}
                  >
                    MetroRahi
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(255,255,255,0.8)',
                      lineHeight: 1.6,
                      mb: 3,
                    }}
                  >
                    Your intelligent metro companion for seamless navigation and real-time updates. 
                    Experience the future of public transportation.
                  </Typography>
                </Box>

                {/* Feature highlights */}
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ width: 24, height: 24, bgcolor: '#4CAF50', mr: 2 }}>
                      <Speed />
                    </Avatar>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      Real-time Updates
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ width: 24, height: 24, bgcolor: '#FF9800', mr: 2 }}>
                      <Security />
                    </Avatar>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      Secure & Private
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                bgcolor: 'transparent',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 4,
                overflow: 'visible',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography
                  variant="h5"
                  component="h3"
                  sx={{
                    fontWeight: 700,
                    mb: 3,
                    color: 'white',
                  }}
                >
                  Quick Links
                </Typography>
                <Stack spacing={2}>
                  <Button
                    component={RouterLink}
                    to="/admin"
                    sx={{
                      color: 'rgba(255,255,255,0.8)',
                      justifyContent: 'flex-start',
                      p: 0,
                      textTransform: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        color: 'white',
                        bgcolor: 'rgba(255,255,255,0.1)',
                      },
                    }}
                  >
                    Admin Panel
                  </Button>
                  <Button
                    component="a"
                    href="/privacy"
                    sx={{
                      color: 'rgba(255,255,255,0.8)',
                      justifyContent: 'flex-start',
                      p: 0,
                      textTransform: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        color: 'white',
                        bgcolor: 'rgba(255,255,255,0.1)',
                      },
                    }}
                  >
                    Privacy Policy
                  </Button>
                  <Button
                    component="a"
                    href="/terms"
                    sx={{
                      color: 'rgba(255,255,255,0.8)',
                      justifyContent: 'flex-start',
                      p: 0,
                      textTransform: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        color: 'white',
                        bgcolor: 'rgba(255,255,255,0.1)',
                      },
                    }}
                  >
                    Terms of Service
                  </Button>
                  <Button
                    component="a"
                    href="/support"
                    sx={{
                      color: 'rgba(255,255,255,0.8)',
                      justifyContent: 'flex-start',
                      p: 0,
                      textTransform: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        color: 'white',
                        bgcolor: 'rgba(255,255,255,0.1)',
                      },
                    }}
                  >
                    Support Center
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Download App */}
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                bgcolor: 'transparent',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 4,
                overflow: 'visible',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Typography
                  variant="h5"
                  component="h3"
                  sx={{
                    fontWeight: 700,
                    mb: 3,
                    color: 'white',
                  }}
                >
                  Download App
                </Typography>
                <Stack spacing={2}>
                  <Button
                    component="a"
                    href="https://apps.apple.com/app/metrorahi"
                    target="_blank"
                    startIcon={<Apple />}
                    sx={{
                      color: 'rgba(255,255,255,0.8)',
                      justifyContent: 'flex-start',
                      p: 0,
                      textTransform: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        color: 'white',
                        bgcolor: 'rgba(255,255,255,0.1)',
                      },
                    }}
                  >
                    App Store
                  </Button>
                  <Button
                    component="a"
                    href="https://play.google.com/store/apps/details?id=com.metrorahi.app"
                    target="_blank"
                    startIcon={<Android />}
                    sx={{
                      color: 'rgba(255,255,255,0.8)',
                      justifyContent: 'flex-start',
                      p: 0,
                      textTransform: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        color: 'white',
                        bgcolor: 'rgba(255,255,255,0.1)',
                      },
                    }}
                  >
                    Google Play
                  </Button>
                </Stack>

                {/* Social Media */}
                <Box sx={{ mt: 4 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      color: 'white',
                    }}
                  >
                    Follow Us
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Button
                      sx={{
                        color: 'rgba(255,255,255,0.8)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: 2,
                        minWidth: 40,
                        '&:hover': {
                          color: 'white',
                          borderColor: 'white',
                          bgcolor: 'rgba(255,255,255,0.1)',
                        },
                      }}
                      component="a"
                      href="https://github.com/metrorahi"
                      target="_blank"
                    >
                      <GitHub />
                    </Button>
                    <Button
                      sx={{
                        color: 'rgba(255,255,255,0.8)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: 2,
                        minWidth: 40,
                        '&:hover': {
                          color: 'white',
                          borderColor: 'white',
                          bgcolor: 'rgba(255,255,255,0.1)',
                        },
                      }}
                      component="a"
                      href="https://linkedin.com/company/metrorahi"
                      target="_blank"
                    >
                      <LinkedIn />
                    </Button>
                    <Button
                      sx={{
                        color: 'rgba(255,255,255,0.8)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: 2,
                        minWidth: 40,
                        '&:hover': {
                          color: 'white',
                          borderColor: 'white',
                          bgcolor: 'rgba(255,255,255,0.1)',
                        },
                      }}
                      component="a"
                      href="https://twitter.com/metrorahi"
                      target="_blank"
                    >
                      <Twitter />
                    </Button>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Divider */}
        <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.2)' }} />

        {/* Bottom Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            © 2024 MetroRahi. All rights reserved.
          </Typography>
          
          <Stack direction="row" spacing={2}>
            <Chip
              label="v2.1.0"
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.8)',
                fontWeight: 500,
              }}
            />
            <Chip
              label="Production"
              size="small"
              sx={{
                bgcolor: '#4CAF50',
                color: 'white',
                fontWeight: 500,
              }}
            />
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer; 